import { FormEvent, useState, useEffect } from 'react';
import Button from '../shared/Button';
import { useBookmarks } from '@/hooks/useBookmarks.ts';
import { useUrlMetadata } from '@/hooks/useUrlMetadata.ts';
import { useTagsAndCategories } from '@/hooks/useTagsAndCategories.ts';


interface CompactAddBookmarkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onBookmarkAdded?: () => void;
  initialUrl?: string;
}

export default function CompactAddBookmarkDialog({
  isOpen,
  onClose,
  onBookmarkAdded,
  initialUrl,
}: CompactAddBookmarkDialogProps) {
  const [url, setUrl] = useState(initialUrl || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update URL when dialog opens with new initial URL
  useEffect(() => {
    if (isOpen && initialUrl) {
      setUrl(initialUrl);
    }
  }, [isOpen, initialUrl]);
  const { createBookmark } = useBookmarks();
  const { fetchMetadata, loading: metadataLoading } = useUrlMetadata();
  const { fetchTagsAndCategories } = useTagsAndCategories();

  const handleUrlSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch metadata
      const metadata = await fetchMetadata(url.trim());

      // Fetch tags and categories
      const tagsAndCategories = await fetchTagsAndCategories(url.trim(), metadata);

      // Create bookmark
      const result = await createBookmark({
        ...metadata,
        url: url.trim(),
        type: 'url',
        source: metadata?.source || 'web',
        tags: tagsAndCategories?.tags,
        categories: tagsAndCategories?.categories,
      });

      if (result?.id) {
        setUrl('');
        onBookmarkAdded?.();
      } else {
        setError('Failed to create bookmark');
      }
    } catch (err) {
      setError('Failed to create bookmark');
      console.error('Error creating bookmark:', err);
    } finally {
      setLoading(false);
    }
  };

  const isProcessing = loading || metadataLoading;

  if (!isOpen) return null;

  return (
    <div className="fixed top-20 right-4 bg-[var(--color-background-modal)] rounded-lg w-80 p-4 shadow-xl border border-[var(--color-neutral-light-gray)] z-[60]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-[var(--color-text)]">Add Bookmark</h2>
          <Button variant="text" size="sm" className="rounded-full p-1" onClick={onClose}>
            <span className="sr-only">Close</span>
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </Button>
        </div>

        {error && (
          <div className="mb-3 p-2 rounded text-sm bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        <form onSubmit={handleUrlSubmit}>
          <input
            type="url"
            placeholder="Enter URL"
            value={url}
            onChange={(e) => setUrl(e.currentTarget.value)}
            className="w-full px-3 py-2 mb-3 rounded border border-[var(--color-neutral-light-gray)] bg-[var(--color-background)] text-[var(--color-text)] placeholder-[var(--color-text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
            required
          />

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            disabled={isProcessing}
          >
            {isProcessing ? 'Adding...' : 'Add Bookmark'}
          </Button>
        </form>
    </div>
  );
}
